// chat/Chat.tsx — the chat panel UI (cloned from Oriora Expense's src/chat/Chat.tsx). Purely
// ADDITIVE: it drives the SAME capabilities the app already has (add / edit / delete / filter /
// reflect) by typing. All styling is namespaced under .jcp-* so it can't collide with the existing
// Journal CSS (.card / .row / .btn / .sheet / .entry-row …).
//
// HITL: a delete proposed by the router is held in `pending` and only runs on an explicit Confirm
// tap (a chat message can't destroy an entry on its own) — mirrors the timeline's delete dialog.

import { useState, useRef, useEffect } from "react";
import { askBrain, resolveTarget, missMessage, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import CallOverlay from "../live/CallOverlay";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type FilterRow } from "./actions";

const STORE_KEY = "oriora.journal.chat.v1"; // device-only; lost on clear/reinstall

const fmtShort = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const GREETING: ChatTurn = {
  role: "assistant",
  content:
    // ⚠️ Never promise that something "never leaves the device" — the promise is that Oriora
    // doesn't RETAIN it (locked fleet-wide 2026-07-10). Name what each ask sends, honestly.
    "Tell me what to do — I'll add it, find it, recap what you wrote, or reflect on your week.\n\n" +
    'e.g. "add entry Long walk by the river, mood 4" · "what was on my mind in March?" · "reflect on my week".\n\n' +
    "Your writing stays in the app; when you ask about it, the matching entries are sent once for that answer — Oriora doesn't retain them.",
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const parsed = JSON.parse(raw); if (Array.isArray(parsed) && parsed.length) return parsed; }
  } catch { /* ignore — start fresh */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
const destructive = (k: ChatAction["kind"]) => k === "backup_restore" || k === "import_file" || k === "delete_entry" ||
        k === "library_remove" || k === "library_merge";

const warnFor = (action: ChatAction): string =>
  
  action.kind === "library_merge"
    ? `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : action.kind === "library_remove"
      ? `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
      : `Delete ${action.kind === "delete_entry" ? (action.label ?? "this entry") : "this"}? This can't be undone — tap Confirm, or Cancel to keep it.`;

const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";

// A held destructive action PLUS the rest of its queue and the transcript it was asked with.
// Confirm runs the action then RESUMES the queue (re-holding at any further destructive);
// Cancel drops the tail and says so. Without the tail, "delete Monday's entry and back up"
// silently never backs up — the exact G17 half-work class.
type PendingHold = { action: ChatAction; rest: ChatAction[]; transcript: string };

export default function Chat({ onChanged }: { onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  // A delete is held here until the user taps Confirm — the router proposing it is NOT enough.
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a journal entry. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  // A photo turn is filed ON-DEVICE onto a journal entry: no model call, no cost. The user names the
  // entry (or "today"/"yesterday") in the same message; fuzzy name-resolution finds the closest one.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const q = text.trim() || "today";
      const rows = await resolveTarget(q);
      if (rows.length === 0) {
        const miss = text.trim() ? await missMessage(q) : "There's no entry for today yet — write one first, then I'll attach the photo.";
        setTurns([...next, { role: "assistant", content: miss }]);
        return; // keep the photo attached
      }
      if (rows.length > 1) {
        const list = rows.slice(0, 6).map((e) => `• "${e.title}" (${e.created_at.slice(0, 10)})`).join("\n");
        setTurns([...next, { role: "assistant", content: `More than one entry matches — which one?\n${list}` }]);
        return; // keep the photo attached
      }
      const target = rows[0];
      await store.updateEntry(target.id, { photo_ref: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Added your photo to "${target.title}".` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, onChunk);
      const transcript = next.map((t) => `${t.role}: ${t.content}`).join("\n");

      // G17: run EVERY action in order — "delete Monday's and Tuesday's" must handle BOTH.
      const r = await runQueue(actions, transcript);
      if (r.hold) {
        setPending(r.hold);
        const warn = warnFor(r.hold.action) + tailNote(r.hold.rest);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warn].filter(Boolean).join("\n\n"), rows: r.rows, noHistory: r.sensitive || undefined }]);
      } else {
        const body = [reply, ...r.lines].filter(Boolean).join("\n\n");
        setTurns([...next, { role: "assistant", content: body || "Done.", rows: r.rows, noHistory: r.sensitive || undefined }]);
      }
      if (r.changed) onChanged?.();
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  // Run actions in order until the first destructive one, which is returned as a hold with its tail.
  async function runQueue(queue: ChatAction[], transcript: string) {
    const lines: string[] = [];
    let rows: FilterRow[] | undefined;
    let changed = false;
    let sensitive = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action.kind)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, sensitive, hold: { action: held, rest: queue.slice(i + 1), transcript } as PendingHold };
      }
      const result = await executeAction(action, { transcript });
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
      if (result.sensitive) sensitive = true;
    }
    return { lines, rows, changed, sensitive, hold: null };
  }

  async function confirmPending() {
    if (!pending || busy) return;
    const { action, rest, transcript } = pending;
    setPending(null);
    setBusy(true);
    try {
      const result = await executeAction(action, { transcript });
      const lines = result.message ? [result.message] : [];
      let rows = result.rows;
      let changed = !!result.changed;
      let sensitive = !!result.sensitive;
      // Resume the rest of the request — a further destructive action re-holds, same rule.
      const r = await runQueue(rest, transcript);
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      changed = changed || r.changed;
      sensitive = sensitive || r.sensitive;
      if (r.hold) {
        setPending(r.hold);
        lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest));
      }
      setTurns((t) => [...t, { role: "assistant", content: lines.join("\n\n") || "Done.", rows, noHistory: sensitive || undefined }]);
      if (changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, {
      role: "assistant",
      content: skipped
        ? `Okay — kept it. I also skipped the ${skipped} remaining step${skipped > 1 ? "s" : ""} of that request; ask again if you still want ${skipped > 1 ? "them" : "it"}.`
        : "Okay — kept it. Tell me what else to do.",
    }]);
  }

  return (
    <div className="jcp-chat">
      <div className="jcp-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`jcp-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`jcp-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="jcp-msgrow"><div className="jcp-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="jcp-composer jcp-confirm">
          <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={confirmPending} disabled={busy}>{pending && String(pending.action.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="jcp-composer">
            <textarea
              value={input}
              placeholder={photo ? 'Name the entry, or "today"…' : "Tell me what to do…"}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. The gate here used to be iOS-only because no other path existed. */}
            {<button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
          </div>
        </>
      )}
      {onCall && <CallOverlay
        onClose={() => setOnCall(false)}
        onTranscript={(lines, seconds) => {
          // The finished call becomes part of the chat history — hang up and the whole
          // conversation is right there to scroll. Device-only, like every other turn.
          const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
          setTurns((prev) => [
            ...prev,
            { role: "assistant", content: `📞 Live call · ${mm}:${ss}` },
            ...lines.map((l) => ({
              role: l.role === "you" ? ("user" as const) : ("assistant" as const),
              content: l.text.trim(),
            })),
          ]);
        }}
      />}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="jcp-rows">
      {rows.map((r) => (
        <div key={r.id} className="jcp-rowitem">
          <span className="jcp-rowmain">
            <span className="jcp-mooddot" style={{ background: `var(--mood-${Math.min(5, Math.max(1, r.mood))})` }} />
            {fmtShort(r.created_at)} · {r.title}
          </span>
          <span className="tnum" style={{ fontWeight: 600 }}>{r.mood}/5</span>
        </div>
      ))}
    </div>
  );
}
